// src/screens/superadmin/subadmins/SubAdmins.jsx
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FaBan,
  FaCheckCircle,
  FaEnvelope,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUpload,
  FaUserShield,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "../../../assets/css/admin/subadmin/SubAdmins.css";
import Button from "../../../components/general/button/Button";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import Pagination from "../../../components/general/pagination/Pagination";
import StatCard from "../../../components/admin/statcard/StatCard";
import AdminLayout from "../../../layouts/AdminLayout.js";
import MyContext from "../../../context/MyContext.js";
import { SubAdminsListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const SubAdminsList = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken, regExpChecker } = useContext(MyContext);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const [subAdminsList, setSubAdminsList] = useState([]);

  // Admin States
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(1);
  const [issuperadmin, setIssuperadmin] = useState(0);
  const [idToUpdateDeleteSubAdmin, setIdToUpdateDeleteSubAdmin] = useState("");

  // Status options
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  //////////SubAdmin Api's

  const viewAllSubAdmins = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/auth/viewAllAdmin`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setSubAdminsList(response.data.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load sub-admins.",
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllSubAdmins();
  }, [authToken]);

  const addSubAdmin = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("status", status ? 1 : 0);
    formData.append("issuperadmin", issuperadmin ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/auth/addAdmin`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Sub-admin added successfully!",
          });
          setShowAddModal(false);
          viewAllSubAdmins();
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add sub-admin.",
        });
        setLoading(false);
      });
  };

  const updateSubAdmin = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("id", idToUpdateDeleteSubAdmin);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("status", status ? 1 : 0);
    formData.append("issuperadmin", issuperadmin ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/auth/updateAdmin`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Sub-admin updated successfully!",
          });
          setShowAddModal(false);
          viewAllSubAdmins();
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update sub-admin.",
        });
        setLoading(false);
      });
  };

  // Stats
  const statsData = [
    {
      icon: FaUserShield,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      label: "Total Sub-Admins",
      value: subAdminsList.length.toString(),
      badge: "All",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      label: "Active",
      value: subAdminsList.filter((s) => s.status == 1).length.toString(),
      badge: "Online",
    },
    {
      icon: FaShieldAlt,
      iconBg: "linear-gradient(135deg, #f3e5f5, #e1bee7)",
      iconColor: "#9c27b0",
      label: "Super Admin",
      value: subAdminsList.filter((s) => s.issuperadmin == 1).length.toString(),
      badge: "Full Access",
    },
    {
      icon: FaBan,
      iconBg: "linear-gradient(135deg, #ffebee, #ffcdd2)",
      iconColor: "#f44336",
      label: "Inactive",
      value: subAdminsList.filter((s) => s.status == 0).length.toString(),
      badge: "Offline",
    },
  ];

  // Filter
  const filteredSubAdmins = subAdminsList.filter((subAdmin) => {
    const matchesSearch =
      subAdmin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subAdmin.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && subAdmin.status == 1) ||
      (filterStatus === "inactive" && subAdmin.status == 0);
    return matchesSearch && matchesStatus;
  });

  const getActions = (subAdmin) => [
    {
      icon: "MdVisibility",
      label: "View Details",
      onClick: () => {
        setSelectedSubAdmin(subAdmin);
        setShowViewModal(true);
      },
    },
    {
      icon: "MdEdit",
      label: "Edit Sub-Admin",
      onClick: () => {
        setIsEditing(true);
        setSelectedSubAdmin(subAdmin);
        setIdToUpdateDeleteSubAdmin(subAdmin.id);
        setName(subAdmin.name);
        setEmail(subAdmin.email);
        setPassword("");
        setStatus(subAdmin.status == 1);
        setIssuperadmin(subAdmin.issuperadmin == 1);
        setShowAddModal(true);
      },
    },
  ];

  const getStatusBadgeClass = (status) => {
    return status == 1
      ? "ksa-subadmin-badge-active"
      : "ksa-subadmin-badge-inactive";
  };

  const getStatusLabel = (status) => {
    return status == 1 ? "Active" : "Inactive";
  };

  if (loading) {
    return (
      <AdminLayout>
        <SubAdminsListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="ksa-subadmin-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Sub-Admins Management</h1>
            <p className="ksa-layout-page-subtitle">
              Manage admin team members and their permissions.
            </p>
          </div>
          <Button
            variant="primary"
            size="medium"
            icon={<FaPlus />}
            iconPosition="left"
            onClick={() => {
              setIsEditing(false);
              setName("");
              setEmail("");
              setPassword("");
              setStatus(1);
              setIssuperadmin(0);
              setIdToUpdateDeleteSubAdmin("");
              setShowAddModal(true);
            }}
          >
            Add Sub-Admin
          </Button>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-4 ksa-subadmin-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* Main Table Card */}
        <div className="ksa-card">
          <div className="ksa-subadmin-table-header">
            <div className="ksa-subadmin-filters">
              <select
                className="ksa-subadmin-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="ksa-subadmin-search">
              <FaSearch size={16} />
              <input
                type="text"
                placeholder="Search sub-admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="ksa-subadmin-table-wrapper">
            <table className="ksa-subadmin-table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>EMAIL</th>
                    <th>SUPER ADMIN</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubAdmins
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((subAdmin) => (
                      <tr key={subAdmin.id}>
                        <td className="ksa-subadmin-name">{subAdmin.name}</td>
                        <td className="ksa-subadmin-email">{subAdmin.email}</td>
                        <td>
                          <span
                            className={`ksa-subadmin-status-badge ${subAdmin.issuperadmin == 1 ? "ksa-subadmin-badge-active" : "ksa-subadmin-badge-inactive"}`}
                          >
                            {subAdmin.issuperadmin == 1 ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`ksa-subadmin-status-badge ${getStatusBadgeClass(subAdmin.status)}`}
                          >
                            {getStatusLabel(subAdmin.status)}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown actions={getActions(subAdmin)} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
          </div>

          <Pagination
            totalItems={filteredSubAdmins.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* Add/Edit Sub-Admin Modal */}
        {showAddModal && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-subadmin-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-subadmin-modal-header">
                <h3 className="ksa-subadmin-modal-title">
                  {isEditing ? "Edit Sub-Admin" : "Add New Sub-Admin"}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-subadmin-modal-body">
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <div className="ksa-subadmin-form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        className="ksa-subadmin-input"
                        placeholder=" Usman Khan"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          setErrorMessage(
                            regExpChecker("username", e.target.value),
                          );
                        }}
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="ksa-subadmin-form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        className="ksa-subadmin-input"
                        placeholder="e.g., usman@farmshop.pk"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setErrorMessage(
                            regExpChecker("email", e.target.value),
                          );
                        }}
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="ksa-subadmin-form-group">
                      <label>Password {!isEditing && "*"}</label>
                      <input
                        type="password"
                        className="ksa-subadmin-input"
                        placeholder={
                          isEditing
                            ? "Leave blank to keep current"
                            : "Enter password"
                        }
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setErrorMessage(
                            regExpChecker("password", e.target.value),
                          );
                        }}
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="ksa-subadmin-form-group">
                      <label>Status</label>
                      <div className="ksa-subadmin-status-toggle">
                        <label className="ksa-subadmin-toggle">
                          <input
                            type="checkbox"
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                          />
                          <span className="ksa-subadmin-toggle-slider"></span>
                        </label>
                        <span>{status ? "Active" : "Inactive"}</span>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="ksa-subadmin-form-group">
                      <label>Is Super Admin</label>
                      <div className="ksa-subadmin-status-toggle">
                        <label className="ksa-subadmin-toggle">
                          <input
                            type="checkbox"
                            checked={issuperadmin}
                            onChange={(e) => setIssuperadmin(e.target.checked)}
                          />
                          <span className="ksa-subadmin-toggle-slider"></span>
                        </label>
                        <span>{issuperadmin ? "Yes" : "No"}</span>
                      </div>
                        {errorMessage && (
                          <p style={{ color: "red", fontSize: "0.875rem" }}>{errorMessage}</p>
                        )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ksa-subadmin-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={isEditing ? updateSubAdmin : addSubAdmin}
                  disabled={loading}
                >
                  {loading
                    ? "Please wait..."
                    : isEditing
                      ? "Update Sub-Admin"
                      : "Create Sub-Admin"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* View Sub-Admin Details Modal */}
        {showViewModal && selectedSubAdmin && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-md ksa-subadmin-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-subadmin-modal-header">
                <h3 className="ksa-subadmin-modal-title">Sub-Admin Profile</h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-subadmin-modal-body">
                <div className="ksa-subadmin-profile-header">
                  <div className="ksa-subadmin-profile-info">
                    <h4>{selectedSubAdmin.name}</h4>
                    <span
                      className={`ksa-subadmin-status-badge ${getStatusBadgeClass(selectedSubAdmin.status)}`}
                    >
                      {getStatusLabel(selectedSubAdmin.status)}
                    </span>
                  </div>
                </div>

                <div className="ksa-subadmin-details-section">
                  <h5>Information</h5>
                  <div className="ksa-subadmin-detail-item">
                    <FaEnvelope />
                    <div>
                      <label>Email</label>
                      <p>{selectedSubAdmin.email}</p>
                    </div>
                  </div>
                  <div className="ksa-subadmin-detail-item">
                    <FaShieldAlt />
                    <div>
                      <label>Is Super Admin</label>
                      <p>{selectedSubAdmin.issuperadmin == 1 ? "Yes" : "No"}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ksa-subadmin-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowViewModal(false);
                    setIsEditing(true);
                    setIdToUpdateDeleteSubAdmin(selectedSubAdmin.id);
                    setName(selectedSubAdmin.name);
                    setEmail(selectedSubAdmin.email);
                    setPassword("");
                    setStatus(selectedSubAdmin.status == 1);
                    setIssuperadmin(selectedSubAdmin.issuperadmin == 1);
                    setShowAddModal(true);
                  }}
                >
                  Edit Sub-Admin
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SubAdminsList;
